import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Form, getForms, createForm, deleteForm, cloneForm, toggleFormStatus, exportFormData } from '../utils/api';
import { generateEmbedUrl } from '../utils';
import { Button, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, Input, Textarea, Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '../components/ui';
import { PlusIcon, ListIcon, MoreHorizontalIcon, TrashIcon, CopyIcon, CodeIcon, LoaderIcon, EditIcon, PublishIcon, UnpublishIcon, ExportIcon } from '../components/Icons';

const FormActions: React.FC<{ form: Form; onClone: (id: string) => void; onDelete: (id: string) => void; onToggleStatus: (id: string) => void; onExport: (id: string) => void }> = ({ form, onClone, onDelete, onToggleStatus, onExport }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showEmbedModal, setShowEmbedModal] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Use utility function to generate correct embed URL
    const embedUrl = generateEmbedUrl(form.id);

    return (
        <div className="relative" ref={dropdownRef}>
            <Button variant="ghost" size="icon" onClick={() => setIsOpen(!isOpen)}>
                <MoreHorizontalIcon className="h-4 w-4" />
            </Button>
            {isOpen && (
                <div className="absolute right-0 bottom-full mb-2 w-48 bg-background rounded-md shadow-lg border z-10 p-1">
                    <button onClick={() => { onClone(form.id); setIsOpen(false); }} className="w-full text-left flex items-center gap-2 p-2 text-sm rounded-sm hover:bg-accent">
                        <CopyIcon className="h-4 w-4" /> Clone Form
                    </button>
                    <button onClick={() => { onToggleStatus(form.id); setIsOpen(false); }} className="w-full text-left flex items-center gap-2 p-2 text-sm rounded-sm hover:bg-accent">
                        {form.status === 'published' ? (
                            <>
                                <UnpublishIcon className="h-4 w-4" /> Make Private
                            </>
                        ) : (
                            <>
                                <PublishIcon className="h-4 w-4" /> Publish
                            </>
                        )}
                    </button>
                    <button 
                        className="w-full text-left flex items-center gap-2 p-2 text-sm rounded-sm hover:bg-accent" 
                        onClick={() => { 
                            setIsOpen(false); 
                            setShowEmbedModal(true); 
                        }}
                    >
                        <CodeIcon className="h-4 w-4" /> Embed Code
                    </button>
                    <button onClick={() => { onExport(form.id); setIsOpen(false); }} className="w-full text-left flex items-center gap-2 p-2 text-sm rounded-sm hover:bg-accent">
                        <ExportIcon className="h-4 w-4" /> Export Data
                    </button>
                    <div className="my-1 h-px bg-border" />
                    <button onClick={() => { onDelete(form.id); setIsOpen(false); }} className="w-full text-left flex items-center gap-2 p-2 text-sm rounded-sm hover:bg-accent text-destructive">
                        <TrashIcon className="h-4 w-4" /> Delete Form
                    </button>
                </div>
            )}
            <Dialog open={showEmbedModal} onOpenChange={setShowEmbedModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Embed Form</DialogTitle>
                        <DialogDescription>Copy this URL to embed the form on your website or share it directly.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium mb-2 block">Direct Link</label>
                            <Textarea readOnly value={embedUrl} rows={2} className="font-mono text-sm" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={() => { 
                            navigator.clipboard.writeText(embedUrl); 
                            alert('Embed URL copied to clipboard!'); 
                        }}>
                            Copy URL
                        </Button>
                        <DialogClose asChild>
                            <Button variant="outline">Close</Button>
                        </DialogClose>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};


const FormsListPage: React.FC = () => {
    const [forms, setForms] = useState<Form[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newFormName, setNewFormName] = useState('');
    const [newFormDescription, setNewFormDescription] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const navigate = useNavigate();

    const fetchForms = useCallback(async () => {
        setLoading(true);
        try {
            const formsData = await getForms();
            setForms(formsData);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchForms();
    }, [fetchForms]);

    const handleCreateForm = async () => {
        if (!newFormName.trim()) return;
        setIsCreating(true);
        try {
            const newForm = await createForm(newFormName, newFormDescription);
            setShowCreateModal(false);
            setNewFormName('');
            setNewFormDescription('');
            fetchForms(); // Refresh the list instead of navigating
        } catch (err) {
            console.error(err);
        } finally {
            setIsCreating(false);
        }
    };
    
    const handleDeleteForm = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this form and all its submissions?')) {
            try {
                await deleteForm(id);
                fetchForms();
            } catch (err) {
                console.error(err);
            }
        }
    };
    
    const handleCloneForm = async (id: string) => {
        try {
            await cloneForm(id);
            fetchForms();
        } catch (err) {
            console.error(err);
        }
    };

    const handleToggleStatus = async (id: string) => {
        try {
            await toggleFormStatus(id);
            fetchForms();
        } catch (err) {
            console.error(err);
        }
    };

    const handleExportForm = async (id: string) => {
        try {
            const data = await exportFormData(id);
            
            // Create JSON export
            const jsonContent = JSON.stringify(data, null, 2);
            const blob = new Blob([jsonContent], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${data.form.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_export.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
            alert('Failed to export form data');
        }
    };

    if (loading) {
        return <div className="p-8 text-center flex justify-center items-center h-screen"><LoaderIcon className="h-8 w-8" /></div>;
    }

    return (
        <div className="container mx-auto p-4 md:p-8">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold">My Forms</h1>
                <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                    <DialogTrigger asChild>
                        <Button>
                            <PlusIcon className="h-4 w-4 mr-2" />
                            Create Form
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Create New Form</DialogTitle>
                            <DialogDescription>Give your new form a name and an optional description.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div>
                                <label htmlFor="new-form-name" className="block text-sm font-medium mb-1">Form Name</label>
                                <Input id="new-form-name" value={newFormName} onChange={e => setNewFormName(e.target.value)} placeholder="e.g., Contact Us" />
                            </div>
                             <div>
                                <label htmlFor="new-form-desc" className="block text-sm font-medium mb-1">Description (optional)</label>
                                <Textarea id="new-form-desc" value={newFormDescription} onChange={e => setNewFormDescription(e.target.value)} placeholder="e.g., Get in touch with our team." />
                            </div>
                        </div>
                        <DialogFooter>
                            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
                            <Button onClick={handleCreateForm} disabled={isCreating || !newFormName.trim()}>
                                {isCreating && <LoaderIcon className="h-4 w-4 mr-2" />}
                                Create
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
            
            {forms.length === 0 ? (
                <div className="text-center py-16 border-2 border-dashed rounded-lg">
                    <h2 className="text-xl font-semibold mb-2">No forms yet</h2>
                    <p className="text-muted-foreground mb-4">Click "Create Form" to get started.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {forms.map(form => (
                        <Card key={form.id} className="flex flex-col">
                            <CardHeader>
                                <CardTitle className="truncate">{form.name}</CardTitle>
                                <CardDescription className="truncate h-5">{form.description}</CardDescription>
                            </CardHeader>
                            <CardContent className="flex-grow">
                                <div className="flex justify-between items-center text-sm text-muted-foreground">
                                    <span>
                                        {form.submission_count} {form.submission_count === 1 ? 'submission' : 'submissions'}
                                    </span>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${form.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                        {form.status.charAt(0).toUpperCase() + form.status.slice(1)}
                                    </span>
                                </div>
                            </CardContent>
                            <CardFooter className="flex justify-between items-center">
                                <div className="flex gap-2">
                                    <Link to={`/form/${form.id}/responses`}>
                                        <Button variant="outline">
                                            <ListIcon className="h-4 w-4 mr-2" />
                                            Responses
                                        </Button>
                                    </Link>
                                     <Link to={`/form/${form.id}/edit`}>
                                        <Button>
                                            <EditIcon className="h-4 w-4 mr-2" />
                                            Edit
                                        </Button>
                                    </Link>
                                </div>
                                <FormActions 
                                    form={form} 
                                    onClone={handleCloneForm} 
                                    onDelete={handleDeleteForm}
                                    onToggleStatus={handleToggleStatus}
                                    onExport={handleExportForm}
                                />
                            </CardFooter>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

export default FormsListPage;